// Batch / "Import Data" modal. 1-to-1 port of the live editor's 4-tab import
// modal (snapshot L1642-1787): Upload (drag/drop) · Google Sheets · Paste ·
// Manual table → then a field-tagging + collapsible data-preview phase, with an
// "Export PDF" action. Built on the shared modal contract (ui/dom.ts openModal)
// + inline-SVG icons (ui/icons.ts), wired to the existing batch engine.
import { el, openModal, closeModal, toast } from './../ui/dom'
import { state, getCanvas } from './../state'
import { labelPx } from './../canvas'
import { parseBatchFile, parseDelimitedText, fetchGoogleSheet } from './import'
import { detectBatchFields } from './engine'
import { batchExportPDF } from './export'
import { saveState } from './../history'
import { addText, isTextObj } from './../widgets/text'
import { isDocumentDesign } from './../render-api'
import { ensureAdGate } from './../ads/gate'
import type { BatchRow } from './../types'

/** Notify listeners (toolbar Field menu) that the imported dataset changed. */
function announceBatchChange(): void {
  document.dispatchEvent(new CustomEvent('olm:batchchange'))
}

/** Documents (A4/Letter pages, multi-page, data tables) don't batch-export
 *  one-label-per-row — their PDF comes from the toolbar Export instead. */
function isDocumentContext(): boolean {
  return isDocumentDesign() || state.currentSize.category === 'document'
}

export function closeBatchModal(): void {
  closeModal()
}

export function openBatchModal(): void {
  // Body host — openModal handles the overlay/title/close chrome.
  const body = el('div', 'flex flex-col gap-3')
  body.style.minHeight = '320px'

  // Subtitle (snapshot: "Load data from Excel, CSV…")
  const subtitle = el('p', 'text-xs text-slate-400 -mt-2',
    'Load data from Excel, CSV, or Google Sheets — then export unique labels for each row')

  // Two phases share this single body; we re-render in place.
  const phaseHost = el('div', 'flex-1')

  // Footer: Export PDF (one label per row) for LABEL designs. For documents
  // (A4 pages / data tables) batch export is the wrong path — the PDF comes
  // from the toolbar Export — so the footer is just "Done".
  const docCtx = isDocumentContext()
  const exportBtn = el('button', 'tb-btn primary', 'Export PDF') as HTMLButtonElement
  exportBtn.disabled = state.batchData.length === 0
  exportBtn.addEventListener('click', () => doExport(exportBtn))
  const doneBtn = el('button', 'tb-btn primary', 'Done') as HTMLButtonElement
  doneBtn.addEventListener('click', () => closeModal())

  body.append(subtitle, phaseHost)

  openModal({
    title: 'Import Data',
    body,
    width: '820px',
    testid: 'batch-modal',
    actions: [docCtx ? doneBtn : exportBtn],
  })

  const render = () => {
    phaseHost.replaceChildren()
    if (state.batchData.length === 0) {
      phaseHost.append(buildImportPhase(render, exportBtn))
    } else {
      phaseHost.append(buildFieldsPhase(render, exportBtn))
    }
    exportBtn.disabled = state.batchData.length === 0
  }
  render()
}

// ---------------------------------------------------------------------------
// PHASE 1 — Import (Upload / Google Sheets / Paste / Manual)
// ---------------------------------------------------------------------------

type ImportTab = 'upload' | 'gsheets' | 'paste' | 'manual'

function buildImportPhase(rerender: () => void, _exportBtn: HTMLButtonElement): HTMLElement {
  const wrap = el('div')

  const hint = el('div', 'text-xs text-slate-500 mb-3',
    '💡 Import your data — we’ll help you place the fields on your label.')

  // Tab bar (.batch-tab)
  const tabBar = el('div', 'flex gap-2 mb-3')
  const tabDefs: { id: ImportTab; label: string }[] = [
    { id: 'upload', label: '📁 Upload File' },
    { id: 'gsheets', label: '🔗 Google Sheets' },
    { id: 'paste', label: '📋 Paste' },
    { id: 'manual', label: '✏️ Manual' },
  ]
  const panel = el('div', 'batch-import-tab')

  const loadParsed = (parsed: { headers: string[]; rows: BatchRow[] }) => {
    state.batchData = parsed.rows
    state.batchHeaders = parsed.headers
    state.batchFields = detectBatchFields()
    announceBatchChange()
    rerender()
  }

  const tabBtns: HTMLButtonElement[] = []
  let active: ImportTab = 'upload'
  const showTab = (id: ImportTab) => {
    active = id
    tabBtns.forEach((b, i) => b.classList.toggle('active', tabDefs[i].id === id))
    panel.replaceChildren(buildImportTabBody(id, loadParsed, showTab))
  }
  for (const def of tabDefs) {
    const b = el('button', 'batch-tab', def.label) as HTMLButtonElement
    b.type = 'button'
    b.addEventListener('click', () => showTab(def.id))
    tabBtns.push(b)
    tabBar.append(b)
  }

  wrap.append(hint, tabBar, panel)
  showTab(active)
  return wrap
}

function buildImportTabBody(
  id: ImportTab,
  loadParsed: (p: { headers: string[]; rows: BatchRow[] }) => void,
  switchTab: (id: ImportTab) => void,
): HTMLElement {
  switch (id) {
    case 'upload':
      return buildUploadTab(loadParsed)
    case 'gsheets':
      return buildGsheetsTab(loadParsed)
    case 'paste':
      return buildPasteTab(loadParsed, switchTab)
    case 'manual':
      return buildManualTab(loadParsed)
  }
}

function buildUploadTab(loadParsed: (p: { headers: string[]; rows: BatchRow[] }) => void): HTMLElement {
  const wrap = el('div')

  const fileInput = el('input') as HTMLInputElement
  fileInput.type = 'file'
  fileInput.accept = '.xlsx,.xls,.csv,.tsv,.ods'
  fileInput.className = 'hidden'

  const status = el('div', 'text-xs mt-2')

  const dropzone = el('div', 'batch-dropzone')
  dropzone.innerHTML =
    '<div class="text-3xl mb-2">📁</div>' +
    '<div class="text-sm font-medium text-white mb-1">Drop Excel, CSV, or TSV file here</div>' +
    '<div class="text-xs text-slate-400">or click to browse · .xlsx .xls .csv .tsv .ods</div>'
  dropzone.addEventListener('click', () => fileInput.click())
  dropzone.addEventListener('dragover', (e) => { e.preventDefault(); dropzone.classList.add('dragover') })
  dropzone.addEventListener('dragleave', () => dropzone.classList.remove('dragover'))
  dropzone.addEventListener('drop', (e) => {
    e.preventDefault()
    dropzone.classList.remove('dragover')
    const file = e.dataTransfer?.files?.[0]
    if (file) ingestFile(file, status, loadParsed)
  })

  fileInput.addEventListener('change', () => {
    const file = fileInput.files?.[0]
    if (file) ingestFile(file, status, loadParsed)
  })

  wrap.append(dropzone, fileInput, status)
  return wrap
}

async function ingestFile(
  file: File,
  status: HTMLElement,
  loadParsed: (p: { headers: string[]; rows: BatchRow[] }) => void,
): Promise<void> {
  status.className = 'text-xs mt-2 text-slate-400'
  status.textContent = `Reading ${file.name}…`
  try {
    const parsed = await parseBatchFile(file)
    loadParsed(parsed)
  } catch (err) {
    status.className = 'text-xs mt-2 text-red-400'
    status.textContent = `Could not parse file: ${(err as Error).message}`
  }
}

function buildGsheetsTab(loadParsed: (p: { headers: string[]; rows: BatchRow[] }) => void): HTMLElement {
  const wrap = el('div')

  const label = el('label', 'text-xs text-slate-400 block mb-1.5', 'Paste a Google Sheets URL')
  const row = el('div', 'flex gap-2')
  const input = el('input', 'prop-input flex-1') as HTMLInputElement
  input.type = 'url'
  input.placeholder = 'https://docs.google.com/spreadsheets/d/...'
  const fetchBtn = el('button', 'tb-btn primary shrink-0', 'Fetch') as HTMLButtonElement
  fetchBtn.type = 'button'
  row.append(input, fetchBtn)

  const note = el('div', 'text-[10px] text-slate-500 mt-1.5',
    'Sheet must be set to "Anyone with the link can view"')
  const status = el('div', 'text-xs mt-2')

  fetchBtn.addEventListener('click', async () => {
    const url = input.value.trim()
    if (!url) return
    fetchBtn.disabled = true
    fetchBtn.textContent = 'Fetching…'
    status.className = 'text-xs mt-2 text-slate-400'
    status.textContent = ''
    try {
      const parsed = await fetchGoogleSheet(url)
      loadParsed(parsed)
    } catch (err) {
      status.className = 'text-xs mt-2 text-red-400'
      status.textContent = (err as Error).message
    } finally {
      fetchBtn.disabled = false
      fetchBtn.textContent = 'Fetch'
    }
  })

  wrap.append(label, row, note, status)
  return wrap
}

function buildPasteTab(
  loadParsed: (p: { headers: string[]; rows: BatchRow[] }) => void,
  switchTab: (id: ImportTab) => void,
): HTMLElement {
  const wrap = el('div')

  const label = el('label', 'text-xs text-slate-400 block mb-1.5',
    'Paste data from Excel, Google Sheets, or CSV')
  const ta = el('textarea',
    'w-full rounded-lg border border-slate-600 bg-slate-800 text-white text-xs p-3 font-mono outline-none focus:border-blue-500') as HTMLTextAreaElement
  ta.rows = 8
  ta.placeholder = 'name\taddress\tcity\nJohn Smith\t123 Main St\tNew York\nJane Doe\t456 Oak Ave\tLos Angeles'

  const status = el('div', 'text-xs mt-2')
  const btnRow = el('div', 'flex gap-2 mt-2 items-center')
  const parseBtn = el('button', 'tb-btn primary', 'Parse Data') as HTMLButtonElement
  parseBtn.type = 'button'

  // Google Sheets URL helper hint
  const urlHint = el('span', 'text-xs text-blue-400 hidden items-center gap-1')
  const hintLink = el('a', 'underline', 'fetch it instead?')
  hintLink.href = '#'
  hintLink.addEventListener('click', (e) => {
    e.preventDefault()
    const url = ta.value.trim()
    switchTab('gsheets')
    // populate the gsheets input after the tab swaps in
    setTimeout(() => {
      const input = document.querySelector<HTMLInputElement>('.batch-import-tab input[type="url"]')
      if (input) { input.value = url }
    }, 0)
  })
  urlHint.append(document.createTextNode('🔗 Looks like a Google Sheets URL — '), hintLink)

  const isGsheetUrl = (v: string) => /docs\.google\.com\/spreadsheets/.test(v.trim())
  ta.addEventListener('input', () => {
    urlHint.classList.toggle('hidden', !isGsheetUrl(ta.value))
    urlHint.classList.toggle('inline-flex', isGsheetUrl(ta.value))
  })

  parseBtn.addEventListener('click', () => {
    const text = ta.value.trim()
    if (!text) return
    try {
      const parsed = parseDelimitedText(text)
      loadParsed(parsed)
    } catch (err) {
      status.className = 'text-xs mt-2 text-red-400'
      status.textContent = `Could not parse: ${(err as Error).message}`
    }
  })

  btnRow.append(parseBtn, urlHint)
  wrap.append(label, ta, btnRow, status)
  return wrap
}

function buildManualTab(loadParsed: (p: { headers: string[]; rows: BatchRow[] }) => void): HTMLElement {
  const wrap = el('div')

  // Working grid of cells (headers + rows). Starts with sensible defaults.
  let headers = state.batchHeaders.length ? [...state.batchHeaders] : ['name', 'value']
  let rows: string[][] = state.batchData.length
    ? state.batchData.map((r) => headers.map((h) => String(r[h] ?? '')))
    : [headers.map(() => ''), headers.map(() => '')]

  const head = el('div', 'mb-2 flex items-center justify-between')
  head.append(el('span', 'text-xs text-slate-400', 'Enter data manually'))
  const headBtns = el('div', 'flex gap-2')
  const addRowBtn = el('button', 'tb-btn text-xs', '+ Row') as HTMLButtonElement
  addRowBtn.type = 'button'
  addRowBtn.style.cssText = 'background:#1e293b;border:1px solid #334155;padding:4px 10px'
  const seqBtn = el('button', 'tb-btn text-xs', 'Fill {{#}} Sequence') as HTMLButtonElement
  seqBtn.type = 'button'
  seqBtn.style.cssText = 'background:#1e293b;border:1px solid #334155;padding:4px 10px'
  headBtns.append(addRowBtn, seqBtn)
  head.append(headBtns)

  const tableHost = el('div',
    'overflow-x-auto max-h-[300px] overflow-y-auto rounded-lg border border-slate-700')

  const renderTable = () => {
    const table = el('table', 'batch-data-table')
    const thead = el('thead')
    const htr = el('tr')
    headers.forEach((h, ci) => {
      const th = el('th')
      const hi = el('input', 'batch-manual-input') as HTMLInputElement
      hi.value = h
      hi.addEventListener('change', () => { headers[ci] = hi.value })
      th.append(hi)
      htr.append(th)
    })
    thead.append(htr)
    const tbody = el('tbody')
    rows.forEach((r, ri) => {
      const tr = el('tr')
      headers.forEach((_, ci) => {
        const td = el('td')
        const ci2 = el('input', 'batch-manual-input') as HTMLInputElement
        ci2.value = r[ci] ?? ''
        ci2.addEventListener('input', () => { rows[ri][ci] = ci2.value })
        td.append(ci2)
        tr.append(td)
      })
      tbody.append(tr)
    })
    table.append(thead, tbody)
    tableHost.replaceChildren(table)
  }

  addRowBtn.addEventListener('click', () => { rows.push(headers.map(() => '')); renderTable() })
  seqBtn.addEventListener('click', () => {
    rows.forEach((r, i) => { r[0] = String(i + 1) })
    renderTable()
  })

  const useBtn = el('button', 'tb-btn primary mt-2', 'Use this data') as HTMLButtonElement
  useBtn.type = 'button'
  useBtn.addEventListener('click', () => {
    const cleanHeaders = headers.map((h) => h.trim()).filter(Boolean)
    const dataRows: BatchRow[] = rows
      .filter((r) => r.some((c) => c.trim() !== ''))
      .map((r) => {
        const obj: BatchRow = {}
        cleanHeaders.forEach((h, ci) => { obj[h] = r[ci] ?? '' })
        return obj
      })
    if (!dataRows.length) { toast('Add at least one row of data'); return }
    loadParsed({ headers: cleanHeaders, rows: dataRows })
  })

  renderTable()
  wrap.append(head, tableHost, useBtn)
  return wrap
}

// ---------------------------------------------------------------------------
// PHASE 2 — Field tagging + collapsible data preview
// ---------------------------------------------------------------------------

function buildFieldsPhase(rerender: () => void, exportBtn: HTMLButtonElement): HTMLElement {
  const docCtx = isDocumentContext()
  const wrap = el('div')

  const usedFields = new Set(detectBatchFields())
  const allColumns = state.batchHeaders

  // ---- Step 1: Add fields to label ----
  const step1 = el('div', 'rounded-lg border-2 border-blue-500/40 bg-blue-500/5 p-4 mb-4')
  const s1head = el('div', 'flex items-center gap-2 mb-2')
  s1head.append(
    el('span', 'w-6 h-6 rounded-full bg-blue-500 text-white flex items-center justify-center text-xs font-bold shrink-0', '1'),
    el('div', 'text-sm font-semibold text-white', docCtx ? 'Add fields to your page' : 'Add fields to your label'),
  )
  const s1desc = el('div', 'text-xs text-slate-400 mb-3',
    `Click fields below to place them on your ${docCtx ? 'page' : 'label'}. Each field becomes a text element you can drag, resize, and style.`)

  const tags = el('div', 'flex flex-wrap gap-1.5 mb-2')

  const renderTags = () => {
    tags.replaceChildren()
    for (const col of allColumns) {
      const matched = usedFields.has(col)
      const tag = el('button', `batch-field-tag ${matched ? 'matched' : 'unmatched'}`) as HTMLButtonElement
      tag.type = 'button'
      tag.title = matched ? `{{${col}}} is on the label` : `Click to add {{${col}}} to the label`
      tag.append(document.createTextNode(`{{${col}}}`))
      tag.addEventListener('click', () => {
        insertField(col)
        usedFields.add(col)
        renderTags()
        updateInsertAll()
      })
      tags.append(tag)
    }
  }

  const insertAllWrap = el('div')
  const insertAllBtn = el('button', 'text-[11px] text-blue-400 hover:text-blue-300 font-medium',
    '+ Add all remaining fields at once') as HTMLButtonElement
  insertAllBtn.type = 'button'
  insertAllBtn.addEventListener('click', () => {
    const remaining = allColumns.filter((c) => !usedFields.has(c))
    remaining.forEach((c) => { insertField(c); usedFields.add(c) })
    renderTags()
    updateInsertAll()
  })
  insertAllWrap.append(insertAllBtn)

  const updateInsertAll = () => {
    const remaining = allColumns.filter((c) => !usedFields.has(c))
    insertAllWrap.classList.toggle('hidden', remaining.length === 0)
  }

  step1.append(s1head, s1desc, tags, insertAllWrap)
  renderTags()
  updateInsertAll()

  // ---- Step 2: Export ----
  const step2 = el('div', 'rounded-lg border border-slate-700 bg-slate-800/30 p-4 mb-4')
  const s2head = el('div', 'flex items-center gap-2 mb-1')
  s2head.append(
    el('span', 'w-6 h-6 rounded-full bg-slate-600 text-slate-300 flex items-center justify-center text-xs font-bold shrink-0', '2'),
    el('div', 'text-sm font-medium text-slate-300', docCtx ? 'Design, then export your document' : 'Export your labels'),
  )
  const s2desc = el('div', 'text-xs text-slate-500')
  s2desc.innerHTML = docCtx
    ? 'Close this dialog and design your pages — a <strong class="text-slate-300">Data Table</strong> repeats one row per record. ' +
      'Then use <strong class="text-slate-300">Export</strong> in the toolbar to render the document (all pages + paginating tables) as one PDF.'
    : 'When the layout looks right, use <strong class="text-slate-300">Export PDF</strong> below to generate one label per row.'
  step2.append(s2head, s2desc)

  // ---- Collapsible data summary ----
  const summary = el('div', 'mb-3')
  const toggleBtn = el('button', 'flex items-center gap-2 text-xs text-slate-400 hover:text-slate-300 w-full') as HTMLButtonElement
  toggleBtn.type = 'button'
  toggleBtn.append(
    el('span', 'font-semibold text-slate-300', String(state.batchData.length)),
    document.createTextNode(' rows loaded · '),
    el('span', 'font-semibold text-slate-300', String(allColumns.length)),
    document.createTextNode(' columns '),
    el('span', 'text-slate-600', '▼ show/hide data'),
  )
  const clearLink = el('span', 'ml-auto text-red-400 hover:text-red-300', 'Clear')
  clearLink.addEventListener('click', (e) => {
    e.stopPropagation()
    state.batchData = []
    state.batchHeaders = []
    state.batchFields = []
    exportBtn.disabled = true
    announceBatchChange()
    rerender()
  })
  toggleBtn.append(clearLink)

  const tableWrap = el('div', 'hidden mt-2')
  const scroll = el('div', 'overflow-x-auto max-h-[200px] overflow-y-auto rounded-lg border border-slate-700')
  scroll.append(buildDataTable())
  tableWrap.append(scroll)
  toggleBtn.addEventListener('click', () => tableWrap.classList.toggle('hidden'))

  summary.append(toggleBtn, tableWrap)

  // ---- Re-import link ----
  const reimport = el('div', 'text-center')
  const reBtn = el('button', 'text-xs text-slate-500 hover:text-slate-300', 'Import different data') as HTMLButtonElement
  reBtn.type = 'button'
  reBtn.addEventListener('click', () => {
    state.batchData = []
    state.batchHeaders = []
    state.batchFields = []
    exportBtn.disabled = true
    announceBatchChange()
    rerender()
  })
  reimport.append(reBtn)

  wrap.append(step1, step2, summary, reimport)
  return wrap
}

/** Build the read-only preview table from state.batchData (first ~50 rows). */
function buildDataTable(): HTMLElement {
  const table = el('table', 'batch-data-table')
  const thead = el('thead')
  const htr = el('tr')
  for (const h of state.batchHeaders) htr.append(el('th', undefined, h))
  thead.append(htr)
  const tbody = el('tbody')
  for (const row of state.batchData.slice(0, 50)) {
    const tr = el('tr')
    for (const h of state.batchHeaders) tr.append(el('td', undefined, String(row[h] ?? '')))
    tbody.append(tr)
  }
  table.append(thead, tbody)
  return table
}

/** Insert a {{field}} placeholder as a new text object on the canvas.
 *  Placements CASCADE (column-wise, wrapping) so inserting several fields —
 *  or "add all" — doesn't stack them on top of each other. Shared with the
 *  toolbar's Field ▾ menu. */
export function insertFieldOnCanvas(field: string): void {
  const t = addText()
  t.set({ text: `{{${field}}}` } as any)
  const canvas = getCanvas()
  const { w, h } = labelPx()
  // count existing {{field}} placeholder texts to pick the next free slot
  const n = canvas.getObjects()
    .filter((o: any) => o !== t && isTextObj(o) && /^\{\{[\w.]+\}\}$/.test(o.text ?? '')).length
  const rowH = 30
  const perCol = Math.max(1, Math.floor((h * 0.84) / rowH))
  t.set({
    left: w * 0.08 + Math.floor(n / perCol) * w * 0.3,
    top: h * 0.08 + (n % perCol) * rowH,
  })
  t.setCoords()
  canvas.requestRenderAll()
  saveState()
  state.batchFields = detectBatchFields()
}
const insertField = insertFieldOnCanvas

// ---------------------------------------------------------------------------
// Export action (preserved)
// ---------------------------------------------------------------------------

async function doExport(btn: HTMLButtonElement): Promise<void> {
  if (state.batchData.length === 0) return
  if (await ensureAdGate('batch-export') === 'deny') return
  btn.disabled = true
  const original = btn.textContent
  btn.textContent = 'Generating…'
  try {
    const n = await batchExportPDF('batch.pdf')
    btn.textContent = `Exported ${n} labels ✓`
    toast(`Exported ${n} labels`)
  } catch (err) {
    btn.textContent = 'Export failed'
    toast(`Export failed: ${(err as Error).message}`)
  } finally {
    setTimeout(() => { btn.textContent = original; btn.disabled = state.batchData.length === 0 }, 1500)
  }
}
